import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FiltersBar } from '@rocco/ui/filter-sort/components/filters-bar/FiltersBar';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { FiltersModal } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import type { SortOption } from '@rocco/types/enum/sort-option';
import { ChevronRightIcon } from 'lucide-react';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';

interface FiltersContextType {
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    displayMode: DisplayMode;
    popoverVariant?: 'default' | 'glassy' | 'white';
    popoverBackground?: 'default' | 'transparent' | 'white';
    /** Optional component rendered after bar filter pills (desktop only). */
    barTrailingComponent?: React.ReactNode;
    /** Hide dynamic facet filter pills in the bar. */
    hideFacetFilters: boolean;
    /**
     * Whether filter dropdown triggers should show a small badge with the number
     * of selected values in that filter section.
     *
     * This is opt-in to avoid changing the UI on other pages that use Filters.
     */
    showSelectedCountBadge: boolean;
    /** Button variant for the filter modal trigger - 'default' (dark icon-only) or 'primary' (black with "Filters" text) */
    filterButtonVariant: 'default' | 'primary';
    /** Optional className to apply to each filter pill trigger button. */
    filterPillClassName?: string;
    /** Optional className to apply to the filter modal trigger button (e.g. "Show all Filters"). */
    filterButtonClassName?: string;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

export const useFiltersContext = () => {
    const context = useContext(FiltersContext);

    if (!context) {
        throw new Error(
            'useFiltersContext must be used within a FilterDropdownProvider',
        );
    }

    return context;
};

const WRAP_RESET_MARGIN = 48;

interface FiltersProps extends React.PropsWithChildren {
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    displayMode: DisplayMode;
    stickyComponent?: React.ReactNode;
    tabContainerRef?: React.RefObject<HTMLDivElement>;
    secondaryComponent?: React.ReactNode;
    showSortBy?: boolean;
    popoverVariant?: 'default' | 'glassy' | 'white';
    popoverBackground?: 'default' | 'transparent' | 'white';
    hideModalButton?: boolean;
    totalCount?: number;
    /** Show a small count badge on each filter pill indicating selected values (desktop bar only). */
    showSelectedCountBadge?: boolean;
    /** Button variant for the filter modal trigger - 'default' (dark icon-only) or 'primary' (black with "Filters" text) */
    filterButtonVariant?: 'default' | 'primary';
    /** Optional component rendered after bar filter pills (desktop only). */
    barTrailingComponent?: React.ReactNode;
    /** Hide dynamic facet filter pills in the bar. */
    hideFacetFilters?: boolean;
    /** Optional className to apply to each filter pill trigger button. */
    filterPillClassName?: string;
    /** Optional className to apply to the filter modal trigger button (e.g. "Show all Filters"). */
    filterButtonClassName?: string;
    /**
     * Optional location object from Remix Router (pathname, search).
     * Used in Remix contexts; omit for non-Remix contexts.
     */
    location?: { pathname: string; search: string } | null;
    /**
     * When true, prevents pills from wrapping to multiple rows.
     * Instead, enables horizontal scrolling in a single row.
     */
    disableWrap?: boolean;
    /** Optional className to apply to the scrollable pills container (e.g. to override gap). */
    scrollableClassName?: string;
}

export interface FilterSortOption {
    title: string;
    value: SortOption;
    selected: boolean;
}

export const Filters = (props: FiltersProps) => {
    const {
        children,
        searchFilters,
        sortOptions,
        displayMode,
        stickyComponent,
        tabContainerRef,
        secondaryComponent,
        showSortBy,
        popoverVariant,
        popoverBackground,
        hideModalButton,
        location,
        showSelectedCountBadge = false,
        filterButtonVariant = 'default',
        disableWrap = false,
        barTrailingComponent,
        hideFacetFilters = false,
        filterPillClassName,
        filterButtonClassName,
        scrollableClassName,
    } = props;

    const [isOverflowing, setIsOverflowing] = useState(false);
    const [shouldWrap, setShouldWrap] = useState(false);
    const scrollableRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const shouldWrapRef = useRef(false);
    const wrapThresholdRef = useRef<number | null>(null);

    const updateShouldWrap = useCallback((value: boolean) => {
        shouldWrapRef.current = value;
        setShouldWrap(value);
    }, []);

    useEffect(() => {
        const checkOverflow = () => {
            if (scrollableRef.current && containerRef.current) {
                const scrollWidth = scrollableRef.current.scrollWidth;
                const clientWidth = containerRef.current.clientWidth;
                const isOverflowing = scrollWidth > clientWidth;
                setIsOverflowing(isOverflowing);

                // Skip wrapping logic when disableWrap is true
                // This forces single-row horizontal scroll behavior
                if (disableWrap) {
                    return;
                }

                // Enable wrapping when content would overflow the container
                // This allows pills to wrap to the next row when categories get too long
                if (isOverflowing) {
                    if (!shouldWrapRef.current) {
                        updateShouldWrap(true);
                    }
                    wrapThresholdRef.current = clientWidth;
                } else if (
                    shouldWrapRef.current &&
                    wrapThresholdRef.current !== null &&
                    clientWidth > wrapThresholdRef.current + WRAP_RESET_MARGIN
                ) {
                    wrapThresholdRef.current = null;
                    updateShouldWrap(false);
                } else if (!shouldWrapRef.current) {
                    wrapThresholdRef.current = null;
                }
            }
        };

        checkOverflow(); // Initial check

        window.addEventListener('resize', checkOverflow); // Re-check on window resize

        // Use ResizeObserver to detect when children change size
        // This removes the need to include 'children' in the dependency array
        // as size changes in the observed elements will trigger the callback
        const resizeObserver = new ResizeObserver(checkOverflow);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        if (scrollableRef.current) {
            resizeObserver.observe(scrollableRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkOverflow);
            resizeObserver.disconnect();
        };
    }, [updateShouldWrap, disableWrap]);

    const isDesktop = displayMode === DisplayMode.Desktop;

    const handleScrollToEnd = () => {
        if (scrollableRef.current) {
            scrollableRef.current.scrollTo({
                left: scrollableRef.current.scrollWidth,
                behavior: 'smooth',
            });
        }
    };

    /** Memoize context value to prevent unnecessary re-renders of consuming components */
    const contextValue = useMemo(
        () => ({
            searchFilters,
            sortOptions,
            displayMode,
            popoverVariant,
            popoverBackground,
            showSelectedCountBadge,
            filterButtonVariant,
            barTrailingComponent,
            hideFacetFilters,
            filterPillClassName,
            filterButtonClassName,
        }),
        [
            searchFilters,
            sortOptions,
            displayMode,
            popoverVariant,
            popoverBackground,
            showSelectedCountBadge,
            filterButtonVariant,
            barTrailingComponent,
            hideFacetFilters,
            filterPillClassName,
            filterButtonClassName,
        ],
    );

    return (
        <FiltersContext.Provider value={contextValue}>
            <div ref={tabContainerRef} className="relative w-full">
                <div
                    className="grid w-full items-center gap-2"
                    style={{
                        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    }}
                >
                    {!hideModalButton && (
                        <div className="flex items-center">
                            {searchFilters.config.modalFilters.length > 0 && (
                                <FiltersModal showSorting={!showSortBy} />
                            )}
                        </div>
                    )}

                    <div ref={containerRef} className="relative flex min-w-0">
                        <div
                            ref={scrollableRef}
                            className={cn(
                                'scrollbar-hide flex items-center gap-2',
                                scrollableClassName,
                                disableWrap
                                    ? 'flex-nowrap overflow-x-auto'
                                    : shouldWrap
                                      ? 'flex-wrap'
                                      : 'flex-nowrap',
                            )}
                            style={
                                isOverflowing && !shouldWrap && !disableWrap
                                    ? {
                                          maskImage:
                                              'linear-gradient(to right, black, black 90%, transparent)',
                                          WebkitMaskImage:
                                              'linear-gradient(to right, black, black 90%, transparent)',
                                          paddingRight: '4rem',
                                      }
                                    : undefined
                            }
                        >
                            {children}
                            {isDesktop && <FiltersBar />}
                            {/* Spacer for rightmost badge overflow when scrolling */}
                            {disableWrap && (
                                <div
                                    className="w-2 shrink-0"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                        {isOverflowing && !shouldWrap && !disableWrap && (
                            <Button
                                color={'transparent'}
                                onClick={handleScrollToEnd}
                                size={'square36'}
                                layoutMode={'noShrink'}
                            >
                                <ChevronRightIcon className="size-4.5 text-gray-500" />
                            </Button>
                        )}
                    </div>

                    {(stickyComponent ||
                        secondaryComponent ||
                        (showSortBy && isDesktop)) && (
                        <div className="flex items-center gap-2">
                            {!isDesktop &&
                                searchFilters.config.modalFilters.length >
                                    0 && (
                                    <FiltersModal showSorting={!showSortBy} />
                                )}
                            {stickyComponent}
                            {secondaryComponent}
                            {showSortBy && isDesktop && (
                                <SortDropdown
                                    sortOptions={sortOptions}
                                    location={location}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </FiltersContext.Provider>
    );
};
